(() => {
  if (customElements.get('landu-volume-pdp')) return;
  class LanduVolume extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const source = this.querySelector('[data-lv-config]');
      this.root = this.querySelector('[data-lv-root]');
      if (!source || !this.root) return;
      try { this.cfg = JSON.parse(source.textContent); }
      catch { this.root.textContent = 'Pack configuration unavailable.'; return; }
      this.labels = this.cfg.labels || {};
      if (!this.cfg.supported) return;
      this.allVariants = (this.cfg.variants || []).filter(v => !v.requiresSellingPlan);
      this.optionNames = this.cfg.optionNames || [];
      const warehouseName = String(this.cfg.warehouseOptionName || 'Warehouse').trim().toLowerCase();
      this.warehouseIndex = this.optionNames.findIndex(name => String(name).trim().toLowerCase() === warehouseName);
      this.warehouses = this.warehouseIndex >= 0
        ? [...new Set(this.allVariants.map(v => v.options?.[this.warehouseIndex]).filter(Boolean))]
        : [];
      const selectedVariant = this.allVariants.find(v => v.available && String(v.id) === String(this.cfg.defaultVariantId));
      this.activeWarehouse = selectedVariant?.options?.[this.warehouseIndex]
        || this.warehouses.find(value => this.variantsForWarehouse(value).length)
        || this.warehouses[0] || null;
      this.variants = this.variantsForWarehouse(this.activeWarehouse);
      this.tiers = (this.cfg.tiers || []).filter(t => Number(t.count) > 0);
      if (!this.variants.length || !this.tiers.length) { this.root.textContent = this.labels.soldOut || 'Unavailable'; return; }
      const first = this.variants.find(v => String(v.id) === String(this.cfg.defaultVariantId)) || this.variants[0];
      this.active = Math.max(0, Math.min(this.tiers.length - 1, Number(this.cfg.initialTier) || 0));
      this.lines = [{id: first.id, qty: this.count()}];
      this.touched = false;
      this.busy = false;
      this.warehouseUnavailable = false;
      this.quoteCache = new Map();
      this.quoteBatchKey = '';
      this.quoteTimer = null;
      this.quoteAbort = null;
      this.money = amount => new Intl.NumberFormat(this.cfg.locale || 'en', {style: 'currency', currency: this.cfg.currency || 'USD'}).format(amount / 100);
      this.clickHandler = event => this.click(event);
      this.changeHandler = event => this.change(event);
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('change', this.changeHandler);
      this.productInfo = this.closest('.product-info, product-info');
      this.nativePicker = this.productInfo?.querySelector('variant-radios, variant-selects') || null;
      this.nativeOptionHandler = event => this.onNativeOptionChange(event);
      this.productInfo?.addEventListener('change', this.nativeOptionHandler);
      this.variantChangeHandler = event => {
        if (this.warehouseIndex < 0) return;
        const selected = this.allVariants.find(variant => String(variant.id) === String(event.detail?.variant?.id));
        if (!selected) return;
        const warehouse = selected.options?.[this.warehouseIndex];
        if (this.busy) this.pendingWarehouse = warehouse;
        else this.selectWarehouse(warehouse, true);
      };
      document.addEventListener('product:variant-change', this.variantChangeHandler);
      this.draw();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('change', this.changeHandler);
      this.productInfo?.removeEventListener('change', this.nativeOptionHandler);
      document.removeEventListener('product:variant-change', this.variantChangeHandler);
      clearTimeout(this.quoteTimer);
      this.quoteAbort?.abort();
      this.initialized = false;
    }
    escape(value) { return String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char])); }
    count() { return Number(this.tiers[this.active].count); }
    variantsForWarehouse(warehouse) {
      return this.allVariants.filter(v => v.available && (this.warehouseIndex < 0 || String(v.options?.[this.warehouseIndex]) === String(warehouse)));
    }
    variant(id) { return this.variants.find(v => String(v.id) === String(id)); }
    choiceLabel(variant) {
      if (!variant) return '';
      const values = (variant.options || []).filter((value, index) => index !== this.warehouseIndex && value);
      return values.join(' / ') || variant.title || this.cfg.productTitle;
    }
    sum() { return this.lines.reduce((total, line) => total + line.qty, 0); }
    remainingVariants() { const used = new Set(this.lines.map(line => String(line.id))); return this.variants.filter(v => !used.has(String(v.id))); }
    priceForLines(lines) {
      return lines.reduce((sum, line) => sum + (this.variant(line.id)?.price || 0) * line.qty, 0);
    }
    quoteKey(lines) {
      return `${this.cfg.currency || 'USD'}:${(this.cfg.countryCode || '').toUpperCase()}:${lines.map(line => `${line.id}:${line.qty}`).sort().join('|')}`;
    }
    moneyMinor(amount, currency) {
      const number = Number(amount);
      if (!Number.isFinite(number) || number < 0 || currency !== this.cfg.currency) throw new Error('Invalid Shopify quote amount.');
      return Math.round(number * 100);
    }
    async fetchQuote(lines, signal) {
      const requested = new Map(lines.map(line => [String(line.id), Number(line.qty)]));
      const input = {
        lines: lines.map(line => ({merchandiseId: `gid://shopify/ProductVariant/${line.id}`, quantity: line.qty}))
      };
      if (this.cfg.countryCode) input.buyerIdentity = {countryCode: this.cfg.countryCode};
      const query = `mutation LanduBundleQuote($input: CartInput) {
        cartCreate(input: $input) {
          cart {
            totalQuantity
            lines(first: 50) {
              pageInfo { hasNextPage }
              nodes {
                quantity
                merchandise { ... on ProductVariant { id } }
                cost { subtotalAmount { amount currencyCode } }
                discountAllocations(lineLevelOnly: false) { discountedAmount { amount currencyCode } }
              }
            }
          }
          userErrors { message }
        }
      }`;
      const response = await fetch('/api/2026-10/graphql.json', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({query, variables: {input}}),
        signal
      });
      if (!response.ok) throw new Error(`Shopify quote HTTP ${response.status}`);
      const payload = await response.json();
      const result = payload.data?.cartCreate;
      if (payload.errors?.length || result?.userErrors?.length || !result?.cart || result.cart.lines?.pageInfo?.hasNextPage) {
        throw new Error(result?.userErrors?.[0]?.message || 'Shopify quote unavailable.');
      }
      const cart = result.cart;
      const quoted = new Map();
      let before = 0;
      let discount = 0;
      for (const line of cart.lines?.nodes || []) {
        const id = String(line.merchandise?.id || '').split('/').pop();
        quoted.set(id, (quoted.get(id) || 0) + Number(line.quantity));
        before += this.moneyMinor(line.cost?.subtotalAmount?.amount, line.cost?.subtotalAmount?.currencyCode);
        for (const allocation of line.discountAllocations || []) {
          discount += this.moneyMinor(allocation.discountedAmount?.amount, allocation.discountedAmount?.currencyCode);
        }
      }
      if (cart.totalQuantity !== lines.reduce((sum, line) => sum + line.qty, 0) ||
          requested.size !== quoted.size ||
          [...requested].some(([id, quantity]) => quoted.get(id) !== quantity) ||
          before !== this.priceForLines(lines) || discount > before) {
        throw new Error('Shopify quote does not match the current variant selection.');
      }
      return {status: 'ready', total: before - discount, discount};
    }
    scheduleQuotes() {
      if (this.cfg.discountMode !== 'automatic' || !this.cfg.enableLiveQuote || !this.quoteCache) return;
      const projections = this.tiers.map(tier => this.projectedLines(Number(tier.count)));
      const batchKey = projections.map(lines => this.quoteKey(lines)).join(';');
      if (batchKey === this.quoteBatchKey) return;
      this.quoteBatchKey = batchKey;
      clearTimeout(this.quoteTimer);
      this.quoteAbort?.abort();
      const controller = new AbortController();
      this.quoteAbort = controller;
      this.quoteTimer = setTimeout(async () => {
        for (const lines of projections) {
          if (controller.signal.aborted) return;
          const key = this.quoteKey(lines);
          if (this.quoteCache.get(key)?.status === 'ready') continue;
          this.quoteCache.set(key, {status: 'loading'});
          try {
            const quote = await this.fetchQuote(lines, controller.signal);
            if (controller.signal.aborted) return;
            this.quoteCache.set(key, quote);
          } catch (error) {
            if (controller.signal.aborted) return;
            this.quoteCache.set(key, {status: 'unavailable'});
          }
          if (this.isConnected) this.refreshQuoteDisplay();
        }
      }, 400);
    }
    tierPriceMarkup(lines) {
      const subtotal = this.priceForLines(lines);
      const quote = this.cfg.discountMode === 'automatic' && this.cfg.enableLiveQuote
        ? this.quoteCache?.get(this.quoteKey(lines)) : null;
      if (quote?.status === 'ready' && quote.discount > 0) {
        return `<strong>${this.money(quote.total)}</strong><del>${this.money(subtotal)}</del><small>${this.escape(this.labels.quoteTier || 'Your offer price')}</small>`;
      }
      return `<strong>${this.money(subtotal)}</strong><small>${this.escape(quote?.status === 'ready' ? this.labels.quoteNoneShort || 'Offer not applied' : this.labels.beforeDiscounts || 'Regular price')}</small>`;
    }
    tierOfferText(tier, lines) {
      const percent = Number(tier.discountPercent);
      if (!Number.isFinite(percent) || percent <= 0) return '';
      const quote = this.cfg.discountMode === 'automatic' && this.cfg.enableLiveQuote
        ? this.quoteCache?.get(this.quoteKey(lines)) : null;
      if (quote?.status === 'unavailable') {
        return this.labels.offerMismatch || 'Offer details at checkout';
      }
      if (quote?.status === 'ready') {
        const expected = Math.round(this.priceForLines(lines) * percent / 100);
        const tolerance = Math.max(1, lines.reduce((sum, line) => sum + line.qty, 0));
        if (Math.abs(quote.discount - expected) > tolerance) {
          return this.labels.offerMismatch || 'Offer details at checkout';
        }
      }
      return (this.labels.tierOffer || '__PERCENT__% off with __COUNT__+ spools')
        .replace('__PERCENT__', String(percent))
        .replace('__COUNT__', String(tier.count));
    }
    quoteStatusText(quote) {
      if (quote?.status === 'ready') {
        return quote.discount > 0
          ? (this.labels.quoteApplied || 'You save __AMOUNT__ on this pack.').replace('__AMOUNT__', this.money(quote.discount))
          : this.labels.quoteNone || 'This pack is not showing a discount yet. Check the final total at checkout.';
      }
      return quote?.status === 'unavailable'
        ? this.labels.quoteUnavailable || 'We could not confirm your offer price. Check the final total at checkout.'
        : this.labels.quotePending || 'Checking your pack offer…';
    }
    refreshQuoteDisplay() {
      if (!this.root?.querySelector) return;
      this.tiers.forEach((tier, index) => {
        const price = this.root.querySelector(`[data-lv-tier-price="${index}"]`);
        const lines = this.projectedLines(Number(tier.count));
        if (price) price.innerHTML = this.tierPriceMarkup(lines);
        const offer = this.root.querySelector(`[data-lv-tier-offer="${index}"]`);
        if (offer) offer.textContent = this.tierOfferText(tier, lines);
      });
      const quote = this.quoteCache?.get(this.quoteKey(this.lines));
      const ready = quote?.status === 'ready';
      const totalLabel = this.root.querySelector('[data-lv-total-label]');
      const totalPrice = this.root.querySelector('[data-lv-total-price]');
      const status = this.root.querySelector('[data-lv-quote-status]');
      if (totalLabel) totalLabel.textContent = `${ready && quote.discount > 0 ? this.labels.quoteTotal || 'Your pack total' : this.labels.total} · ${this.count()} ${this.labels.spools}`;
      if (totalPrice) totalPrice.textContent = this.money(ready ? quote.total : this.priceForLines(this.lines));
      if (status) status.textContent = this.quoteStatusText(quote);
    }
    mergeLines(lines) {
      const merged = new Map();
      for (const line of lines) {
        const key = String(line.id);
        if (merged.has(key)) merged.get(key).qty += line.qty;
        else merged.set(key, {...line});
      }
      return Array.from(merged.values());
    }
    projectedLines(nextCount) {
      const lines = this.lines.map(line => ({...line}));
      let excess = lines.reduce((sum, line) => sum + line.qty, 0) - nextCount;
      if (excess > 0) {
        for (let index = lines.length - 1; index >= 0 && excess > 0; index--) {
          const taken = Math.min(lines[index].qty, excess);
          lines[index].qty -= taken;
          excess -= taken;
        }
        return lines.filter(line => line.qty > 0);
      } else if (excess < 0) {
        lines[0].qty += -excess;
      }
      return lines;
    }
    resize(nextCount) {
      this.lines = this.projectedLines(nextCount);
    }
    click(event) {
      if (this.busy) return;
      const tier = event.target.closest('[data-lv-tier]');
      if (tier) {
        const next = Number(tier.dataset.lvTier);
        if (!Number.isInteger(next) || next === this.active || next < 0 || next >= this.tiers.length) return;
        this.active = next;
        this.touched = true;
        this.resize(this.count());
        this.draw();
        this.querySelector(`[data-lv-tier="${next}"]`)?.focus();
        return;
      }
      if (event.target.closest('[data-lv-add-color]')) {
        const alternative = this.remainingVariants()[0];
        const donors = this.lines
          .map((line, index) => ({line, index}))
          .filter(item => item.line.qty > 1)
          .sort((a, b) => b.line.qty - a.line.qty);
        if (!alternative || !donors.length) return;
        this.lines[donors[0].index].qty -= 1;
        this.lines.push({id: alternative.id, qty: 1});
        this.touched = true;
        this.draw();
        this.querySelector(`[data-lv-variant="${this.lines.length - 1}"]`)?.focus();
        return;
      }
      if (event.target.closest('[data-lv-one-color]')) {
        this.lines = [{id: this.lines[0].id, qty: this.count()}];
        this.touched = true;
        this.draw();
        this.querySelector('[data-lv-one-color]')?.focus();
        return;
      }
      const step = event.target.closest('[data-lv-step]');
      if (step) {
        const index = Number(step.dataset.index);
        const direction = Number(step.dataset.lvStep);
        if (!Number.isInteger(index) || !this.lines[index] || ![1, -1].includes(direction)) return;
        if (direction === 1) {
          const donor = this.lines.findIndex((line, idx) => idx !== index && line.qty > 0);
          if (donor < 0) return;
          this.lines[donor].qty -= 1;
          this.lines[index].qty += 1;
        } else {
          const recipient = this.lines.findIndex((line, idx) => idx !== index);
          if (recipient < 0) return;
          this.lines[index].qty -= 1;
          this.lines[recipient].qty += 1;
        }
        this.lines = this.lines.filter(line => line.qty > 0);
        this.touched = true;
        this.draw();
        const same = Math.min(index, this.lines.length - 1);
        this.querySelector(`[data-lv-step="${direction}"][data-index="${same}"]`)?.focus();
        return;
      }
      const buy = event.target.closest('[data-lv-buy]');
      if (buy) void this.buy(buy.dataset.lvBuy, buy);
    }
    change(event) {
      const warehouse = event.target.closest('[data-lv-warehouse]');
      if (warehouse && !this.busy) {
        if (!this.selectWarehouse(warehouse.value)) warehouse.value = this.activeWarehouse;
        this.querySelector('[data-lv-warehouse]')?.focus();
        return;
      }
      const target = event.target.closest('[data-lv-variant]');
      if (!target || this.busy) return;
      const index = Number(target.dataset.lvVariant);
      const next = this.variant(target.value);
      if (!this.lines[index] || !next) return;
      const existing = this.lines.findIndex((line, i) => i !== index && String(line.id) === String(next.id));
      if (existing >= 0) {
        this.lines[existing].qty += this.lines[index].qty;
        this.lines.splice(index, 1);
      } else {
        this.lines[index].id = next.id;
      }
      this.touched = true;
      this.draw();
      this.querySelector(`[data-lv-variant="${Math.min(index, this.lines.length - 1)}"]`)?.focus();
    }
    onNativeOptionChange(event) {
      if (this.warehouseIndex < 0) return;
      const target = event.target;
      const picker = target.closest('variant-radios, variant-selects');
      if (!picker || !this.productInfo?.contains(picker)) return;
      this.nativePicker = picker;
      const optionField = target.closest('[data-option-name], [data-option-position]');
      const optionName = target.dataset.optionName || optionField?.dataset.optionName || target.selectedOptions?.[0]?.dataset.optionName;
      const position = Number(target.dataset.optionPosition || optionField?.dataset.optionPosition);
      const isWarehouse = String(optionName || '').trim().toLowerCase() === String(this.optionNames[this.warehouseIndex]).trim().toLowerCase()
        || position === this.warehouseIndex + 1;
      if (!isWarehouse) return;
      if (this.busy) {
        this.pendingWarehouse = target.value;
        return;
      }
      this.selectWarehouse(target.value, true);
    }
    selectWarehouse(value, native = false) {
      if (String(value) === String(this.activeWarehouse) && !this.warehouseUnavailable) return true;
      const nextVariants = this.variantsForWarehouse(value);
      if (!nextVariants.length) {
        if (native) {
          this.warehouseUnavailable = true;
          this.draw();
        }
        const status = this.querySelector('.lv-status');
        if (status) status.textContent = this.labels.warehouseUnavailable || 'This warehouse is sold out. Choose another warehouse.';
        return false;
      }
      const previous = this.lines.map(line => this.variant(line.id)?.options || []);
      this.activeWarehouse = value;
      this.variants = nextVariants;
      this.warehouseUnavailable = false;
      const fallback = this.variants[0];
      let replaced = false;
      this.lines = this.mergeLines(this.lines.map((line, index) => {
        const options = [...previous[index]];
        options[this.warehouseIndex] = value;
        const matching = this.variants.find(candidate => candidate.options?.every((option, optionIndex) => String(option) === String(options[optionIndex])));
        if (!matching) replaced = true;
        return { ...line, id: (matching || fallback).id };
      }));
      this.touched = true;
      this.draw();
      const status = this.querySelector('.lv-status');
      if (status) status.textContent = replaced
        ? this.labels.warehouseChanged || 'Some colors were unavailable in this warehouse and were replaced. Review your selection.'
        : this.labels.warehouseSelected || 'Warehouse updated. Review your selected colors.';
      return true;
    }
    renderLine(line, index) {
      const v = this.variant(line.id);
      if (!v) return '';
      const canAdjust = this.lines.length > 1;
      const choices = this.allVariants.filter(candidate => this.warehouseIndex < 0 || String(candidate.options?.[this.warehouseIndex]) === String(this.activeWarehouse)).map(candidate => {
        const label = candidate.available
          ? this.choiceLabel(candidate)
          : `${this.choiceLabel(candidate)} — ${this.labels.soldOut || 'Sold out'}`;
        return `<option value="${this.escape(candidate.id)}" ${String(candidate.id) === String(line.id) ? 'selected' : ''} ${candidate.available ? '' : 'disabled'}>${this.escape(label)}</option>`;
      }).join('');
      const thumb = v.image
        ? `<img class="lv-thumb" src="${this.escape(v.image)}" alt="" width="42" height="50" loading="lazy">`
        : '<span class="lv-thumb-placeholder" aria-hidden="true"></span>';
      return `
        <div class="lv-item">
          ${thumb}
          <label class="lv-item-field">
            <span class="lv-item-label">${this.escape(this.labels.color)}</span>
            <select class="lv-variant" data-lv-variant="${index}" aria-label="${this.escape(this.labels.color)} ${index + 1}">${choices}</select>
          </label>
          <div class="lv-item-qty-wrap">
            <span class="lv-item-label">${this.escape(this.labels.spools)}</span>
            <div class="lv-quantity" aria-label="${this.escape(this.labels.spools)}">
              <button type="button" aria-label="${this.escape(this.labels.remove)}" data-lv-step="-1" data-index="${index}" ${canAdjust ? '' : 'disabled'}>−</button>
              <span>${line.qty}</span>
              <button type="button" aria-label="+ ${this.escape(this.labels.spools)}" data-lv-step="1" data-index="${index}" ${canAdjust ? '' : 'disabled'}>+</button>
            </div>
          </div>
          <div class="lv-item-pricing">
            <span>${this.escape(this.labels.unitPrice || 'Unit price')} ${this.money(v.price)}</span>
            <strong>${this.escape(this.labels.lineSubtotal || 'Line subtotal')} ${this.money(v.price * line.qty)}</strong>
          </div>
        </div>`;
    }
    draw() {
      const base = this.priceForLines(this.lines);
      const quoteMode = this.cfg.discountMode === 'automatic' && this.cfg.enableLiveQuote;
      const activeQuote = quoteMode ? this.quoteCache?.get(this.quoteKey(this.lines)) : null;
      const quoteReady = activeQuote?.status === 'ready';
      const count = this.count();
      const canAdd = this.remainingVariants().length > 0 && this.lines.some(line => line.qty > 1);
      const warehouseVariants = this.allVariants.filter(variant => this.warehouseIndex < 0 || String(variant.options?.[this.warehouseIndex]) === String(this.activeWarehouse));
      const soldOutCount = warehouseVariants.length - this.variants.length;
      const builder = `
        <div class="lv-builder">
          <div class="lv-builder-head">
            <strong>${this.escape(this.labels.chooseColor || this.labels.select)}</strong>
            <span>${this.sum()} / ${count} ${this.escape(this.labels.spools)}</span>
          </div>
          ${this.warehouses.length > 1 && !this.nativePicker ? `<label class="lv-warehouse-field"><span class="lv-item-label">${this.escape(this.labels.warehouse || 'Warehouse')}</span><select class="lv-warehouse" data-lv-warehouse aria-label="${this.escape(this.labels.warehouse || 'Warehouse')}">${this.warehouses.map(value => { const available = this.variantsForWarehouse(value).length > 0; return `<option value="${this.escape(value)}" ${String(value) === String(this.activeWarehouse) ? 'selected' : ''} ${available ? '' : 'disabled'}>${this.escape(value)}${available ? '' : ` — ${this.escape(this.labels.soldOut || 'Sold out')}`}</option>`; }).join('')}</select></label>` : ''}
          ${soldOutCount ? `<p class="lv-stock-note">${this.escape(this.labels.soldOutNote || 'Sold-out colors are shown but cannot be selected.')}</p>` : ''}
          <div class="lv-items">${this.lines.map((line, index) => this.renderLine(line, index)).join('')}</div>
          <div class="lv-builder-actions">
            <button type="button" class="lv-action-button" data-lv-add-color ${canAdd ? '' : 'disabled'}>${this.escape(this.labels.addColor)}</button>
            <button type="button" class="lv-action-button" data-lv-one-color ${this.lines.length > 1 ? '' : 'disabled'}>${this.escape(this.labels.oneColor)}</button>
          </div>
        </div>`;
      const tiers = this.tiers.map((candidate, index) => {
        const selected = index === this.active;
        const panelId = `${this.id}-lv-panel-${index}`;
        const projected = this.projectedLines(Number(candidate.count));
        const tierPrice = this.tierPriceMarkup(projected);
        const packName = `${Number(candidate.count)}-${this.escape(this.labels.packSuffix || 'spool pack')}`;
        const subtitle = this.escape(candidate.label || '');
        const offer = this.tierOfferText(candidate, projected);
        return `
          <div class="lv-tier ${selected ? 'is-selected' : ''}">
            <button type="button" class="lv-tier-toggle" data-lv-tier="${index}" aria-expanded="${selected}" aria-controls="${this.escape(panelId)}">
              <span class="lv-radio" aria-hidden="true"></span>
              <span class="lv-tier-info">
                <span class="lv-tier-name-row">
                  <strong class="lv-tier-name">${packName}</strong>
                  ${candidate.badge ? `<span class="lv-tier-badge">${this.escape(candidate.badge)}</span>` : ''}
                </span>
                <span class="lv-tier-sub">${subtitle}</span>
                ${offer ? `<span class="lv-tier-offer" data-lv-tier-offer="${index}">${this.escape(offer)}</span>` : ''}
              </span>
              <span class="lv-tier-price" data-lv-tier-price="${index}">${tierPrice}</span>
            </button>
            <div class="lv-panel" id="${this.escape(panelId)}" ${selected ? '' : 'hidden'}>${selected ? builder : ''}</div>
          </div>`;
      }).join('');
      this.root.innerHTML = `
        <div class="lv-tiers" role="group" aria-label="${this.escape(this.labels.starting)}">${tiers}</div>
        <div class="lv-summary">
          <div class="lv-total-line">
            <span data-lv-total-label>${this.escape(quoteReady && activeQuote.discount > 0 ? this.labels.quoteTotal || 'Your pack total' : this.labels.total)} · ${count} ${this.escape(this.labels.spools)}</span>
            <strong data-lv-total-price>${this.money(quoteReady ? activeQuote.total : base)}</strong>
          </div>
          ${quoteMode ? `<p class="lv-quote-status" data-lv-quote-status role="status" aria-live="polite">${this.escape(this.quoteStatusText(activeQuote))}</p>` : ''}
          <p class="lv-disclaimer">${this.escape(this.labels.standardNote)}</p>
          <div class="lv-buy">
            <button type="button" data-lv-buy="cart" ${this.warehouseUnavailable ? 'disabled' : ''}>${this.escape(this.labels.addToCart)} · ${count}</button>
            ${this.cfg.allowCheckout ? `<button type="button" class="lv-secondary" data-lv-buy="checkout" ${this.warehouseUnavailable ? 'disabled' : ''}>${this.escape(this.labels.checkout)} &nbsp;↗</button>` : ''}
          </div>
          ${this.cfg.allowCheckout ? `<p class="lv-trust-hint">${this.escape(this.labels.payNote || "Made for what's next. Checkout securely with LANDU.")}</p>` : ''}
          <p class="lv-status" role="status" aria-live="polite">${this.warehouseUnavailable ? this.escape(this.labels.warehouseUnavailable || 'This warehouse is sold out. Choose another warehouse.') : ''}</p>
        </div>`;
      this.scheduleQuotes();
    }
    bundleItems() {
      if (this.warehouseUnavailable) throw new Error(this.labels.warehouseUnavailable || 'This warehouse is sold out. Choose another warehouse.');
      const result = new Map();
      for (const line of this.lines) {
        const selected = this.variant(line.id);
        const id = Number(line.id);
        const quantity = Number(line.qty);
        if (!selected || !selected.available || !Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0) {
          throw new Error(this.labels.error || 'Invalid variant selection.');
        }
        result.set(id, (result.get(id) || 0) + quantity);
      }
      const items = Array.from(result, ([id, quantity]) => ({id, quantity}));
      if (!items.length || items.reduce((count, item) => count + item.quantity, 0) !== this.count()) {
        throw new Error(this.labels.selectionError || 'Please complete your pack selection.');
      }
      return items;
    }
    countVariants(cart) {
      if (!cart || !Array.isArray(cart.items)) throw new Error(this.labels.cartError || 'Unable to verify cart contents.');
      const tally = new Map();
      for (const row of cart.items) {
        const id = Number(row.variant_id);
        const quantity = Number(row.quantity);
        if (Number.isSafeInteger(id) && Number.isFinite(quantity) && quantity >= 0) {
          tally.set(id, (tally.get(id) || 0) + quantity);
        }
      }
      return tally;
    }
    async readCart(root) {
      const response = await fetch(this.cfg.cartStateUrl || `${root}cart.js`, {
        method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json'}
      });
      if (!response.ok) throw new Error(this.labels.cartError || 'Unable to verify cart contents.');
      return response.json();
    }
    unlock(status, message, isError = false) {
      status.classList.toggle('is-error', isError);
      status.textContent = message;
      this.querySelectorAll('button,select').forEach(control => { control.disabled = false; });
      const canAdjust = this.lines.length > 1;
      this.querySelectorAll('[data-lv-step]').forEach(control => { control.disabled = !canAdjust; });
      const addColor = this.querySelector('[data-lv-add-color]');
      if (addColor) addColor.disabled = !(this.remainingVariants().length > 0 && this.lines.some(line => line.qty > 1));
      const oneColor = this.querySelector('[data-lv-one-color]');
      if (oneColor) oneColor.disabled = this.lines.length <= 1;
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.querySelectorAll('[data-lv-buy]').forEach(control => { control.disabled = this.warehouseUnavailable; });
      if (this.pendingWarehouse != null) {
        const pending = this.pendingWarehouse;
        this.pendingWarehouse = null;
        this.selectWarehouse(pending, true);
      }
      if (isError) {
        const link = document.createElement('a');
        const root = `${(this.cfg.root || '/').replace(/\/+$/, '')}/`;
        link.href = this.cfg.cartUrl || `${root}cart`;
        link.textContent = this.labels.viewCart || 'View cart';
        status.append(' ', link);
      }
    }
    async buy(destination, opener = null) {
      if (this.busy) return;
      const status = this.querySelector('.lv-status');
      if (!status) return;
      let items;
      try { items = this.bundleItems(); }
      catch (error) {
        status.classList.add('is-error');
        status.textContent = error.message;
        return;
      }
      const root = `${(this.cfg.root || '/').replace(/\/+$/, '')}/`;
      const miniCart = document.querySelector('mini-cart');
      const requestPayload = {items};
      if (destination === 'cart' && miniCart && typeof miniCart.getSectionsToRender === 'function') {
        const sectionNames = miniCart
          .getSectionsToRender()
          .map(section => section.section)
          .filter(Boolean);
        if (sectionNames.length) {
          requestPayload.sections = [...new Set(sectionNames)];
          requestPayload.sections_url = window.location.pathname;
        }
      }
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('button,select').forEach(control => { control.disabled = true; });
      status.classList.remove('is-error');
      status.textContent = this.labels.busy || 'Adding your pack…';
      let posted = false;
      try {
        const before = this.countVariants(await this.readCart(root));
        const response = await fetch(this.cfg.cartAddUrl || `${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {'Content-Type':'application/json', 'Accept':'application/json'},
          body: JSON.stringify(requestPayload)
        });
        const added = await response.json();
        if (!response.ok) throw new Error(typeof added.description === 'string' ? added.description : this.labels.error);
        posted = true;
        const afterCart = await this.readCart(root);
        const after = this.countVariants(afterCart);
        const incorrect = items.filter(item => (after.get(item.id) || 0) - (before.get(item.id) || 0) < item.quantity);
        if (incorrect.length) throw new Error(this.labels.verifyError || 'The pack could not be fully verified.');
        const checkout = `${root}checkout`;
        const cartUrl = this.cfg.cartUrl || `${root}cart`;
        const code = this.cfg.discountMode === 'code' ? String(this.tiers[this.active].code || '').trim() : '';
        if (destination === 'checkout') {
          location.assign(code ? `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(checkout)}` : checkout);
          return;
        }
        if (code) {
          location.assign(`${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cartUrl)}`);
          return;
        }
        if (miniCart && added?.sections && typeof miniCart.renderContents === 'function' &&
            miniCart.getSectionsToRender().every(section => typeof added.sections[section.section] === 'string')) {
          try { miniCart.renderContents(added); }
          catch { location.assign(cartUrl); return; }
          if (window.themeSettings?.redirectToCart) {
            location.assign(cartUrl);
            return;
          }
          try { miniCart.open(opener || document.activeElement); }
          catch { location.assign(cartUrl); return; }
          const eventItems = Array.isArray(added.items) ? added.items : [added];
          document.dispatchEvent(new CustomEvent('cart:item-add', {
            detail: {items: eventItems, context: 'MainProductPage'}
          }));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            void publish(PUB_SUB_EVENTS.cartItemAdd, {source: 'MainProductPage', items: eventItems});
            void publish(PUB_SUB_EVENTS.cartUpdate, {source: 'landu-volume-pdp', cartData: afterCart});
          }
          this.unlock(status, this.labels.added || 'Pack added to cart.');
          return;
        }
        location.assign(cartUrl);
      } catch (error) {
        this.unlock(status, `${error.message || this.labels.error} ${posted ? (this.labels.checkCart || 'Please check your cart before retrying.') : ''}`, true);
      }
    }
  }
  customElements.define('landu-volume-pdp', LanduVolume);
})();
